import React from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import {
  ABOUT_URL, CONTACT_SALES, FRESHDESK_URL,
  HOME_URL,
  LOGIN_URL,
  PERIODIC_REPORT,
  SALES_OVERVIEW_REPORT,
  SETTINGS_BACKUP, SETTINGS_INTEGRATIONS,
  SETTINGS_INVOICES,
  SETTINGS_MACHINES,
  TOP_SALES_PER_CATEGORY_REPORT,
  TOP_SALES_PER_PRODUCT_REPORT,
  TRANSACTIONS_URL, UPGRADE_PAGE,
  USER_SETTINGS_URL,
  Z_REPORTS,
} from '../const/routes';
import AppWrapper from './application/AppWrapper';
import RestrictedRoute from './common/route/restrictedRoute';
import AboutPage from './pages/about/AboutPage';
import LoginPage from './pages/login/LoginPage';
import SettingsPage from './pages/user/settings/SettingsPage';
import '../style/main.scss';
import StoreProvider from '../config/store';
import { AUTH_USER } from '../actions/types';
import ReactDOM from 'react-dom';
import { logout } from '../actions/auth-actions';
import { fetchEndpointAccess, fetchMachines, fetchUser } from '../actions/user-actions';
import MachinesTable from './pages/machines/MachinesTable';
import Backup from './pages/backup/Backup';
import Invoices from './pages/invoices/Invoices';
import { hasAuthenticationHeaders } from '../actions/utils';
import PeriodicReport from './pages/reports/periodic/PeriodicReport';
import ZReports from './pages/reports/zreports/ZReports';
import StandardReportsRoutes from './pages/reports/standard/StandardReportsRoutes';
import AdministrationRoutes from './pages/administration/AdministrationRoutes';
import Transactions from './pages/transactions/Transactions';
import TopSalesReport from './pages/reports/sales/TopSalesReport';
import SalesOverviewReport from './pages/reports/sales/SalesOverviewReport';
import { APP_PATH } from '../config/environment-variables';
import Dashboard from './pages/home/Dashboard';
import CustomPrompt from './common/CustomPrompt';
import ComparisonReportsRoutes from './pages/reports/comparisons/ComparisonReportsRoutes';
import {
  TRANSACTION_LIST,
  TRANSACTION_LIST_SIE_Z_REPORT_LIST,
} from './pages/reports/standard/StandardReportConfig';
import Integrations from './pages/integrations/Integrations';
import UpgradePage from './pages/upgrade/UpgradePage';
import Freshdesk from './pages/freshdesk/FreshdeskPage';
import NewTicket from './pages/freshdesk/NewTicket';
import 'react-quill/dist/quill.snow.css';
import ContactSales from './pages/contact/ContactSales';
import ContactWidget from './pages/contact/ContactWidget';
import Sie4ReportExport from './pages/reports/sie4/Sie4ReportExport';
import Sie4ReportTransactionList from './pages/reports/sie4/Sie4ReportTransactionList';

const applicationStoreStartup = (store) => {
  const authenticationHeaders = hasAuthenticationHeaders();
  
  if (authenticationHeaders) {
    store.dispatch({
      type: AUTH_USER,
      payload: true,
    });
    fetchUser()(store.dispatch)
      .then(() => {
        fetchMachines()(store.dispatch);
        fetchEndpointAccess()(store.dispatch);
      });
  } else {
    logout()(store.dispatch);
  }
};

const App = () => {
  const userConfirmation = React.useCallback((message, callback) => {
    const node = document.getElementById('custom-prompt');
    const cleanUp = (answer) => {
      ReactDOM.unmountComponentAtNode(node);
      callback(answer);
    };
    
    return ReactDOM.render(<CustomPrompt message={message} cleanUp={cleanUp} />, node);
  }, []);
  
  return (
    <StoreProvider
      onStartup={applicationStoreStartup}>
      <BrowserRouter basename={APP_PATH}
                     getUserConfirmation={userConfirmation}>
        <AppWrapper>
          <Switch>
            <Redirect exact from="/v2/default.aspx" to="/v2/" />
            <Redirect exact from="/default.aspx" to={HOME_URL} />
    
            <RestrictedRoute
              exact
              path={HOME_URL}
              component={Dashboard}
            />
  
            <RestrictedRoute
              exact
              path={USER_SETTINGS_URL}
              component={SettingsPage}
            />
            
            <RestrictedRoute
              exact
              path={SETTINGS_MACHINES}
              component={MachinesTable}
            />
            
            <RestrictedRoute
              exact
              path={SETTINGS_INVOICES}
              component={Invoices}
            />
  
            <RestrictedRoute
              exact
              path={PERIODIC_REPORT}
              component={PeriodicReport}
            />
  
            <RestrictedRoute
              exact
              path={TRANSACTION_LIST.urlPath}
              reportDetails={TRANSACTION_LIST}
              component={Sie4ReportTransactionList}
            />
  
            <RestrictedRoute
              exact
              path={TRANSACTION_LIST_SIE_Z_REPORT_LIST.urlPath}
              reportDetails={TRANSACTION_LIST_SIE_Z_REPORT_LIST}
              component={Sie4ReportExport}
            />
  
            <RestrictedRoute
              exact
              path={SETTINGS_BACKUP}
              component={Backup}
            />
  
            <RestrictedRoute
              exact
              path={SETTINGS_INTEGRATIONS}
              component={Integrations}
            />
  
            <RestrictedRoute
              exact
              path={Z_REPORTS}
              component={ZReports}
            />
  
  
            <RestrictedRoute
              exact
              path={SALES_OVERVIEW_REPORT}
              component={SalesOverviewReport}
            />
            
            <RestrictedRoute
              exact
              isCategoryReport
              path={TOP_SALES_PER_CATEGORY_REPORT}
              component={TopSalesReport}
            />
            
            <RestrictedRoute
              exact
              path={TOP_SALES_PER_PRODUCT_REPORT}
              component={TopSalesReport}
            />
  
            <RestrictedRoute
              exact
              path={TRANSACTIONS_URL}
              component={Transactions}
            />
  
            {StandardReportsRoutes()}
  
            {ComparisonReportsRoutes()}
  
            {AdministrationRoutes()}
  
  
            <RestrictedRoute
              exact
              path={`${FRESHDESK_URL}/create`}
              component={NewTicket}
            />
  
            <RestrictedRoute
              exact
              path={`${FRESHDESK_URL}/tickets`}
              component={Freshdesk}
            />
  
            <Route
              exact
              path={ABOUT_URL}
              component={AboutPage}
            />
  
            <RestrictedRoute
              exact
              path={UPGRADE_PAGE}
              component={UpgradePage}
            />
  
            <RestrictedRoute
              exact
              path={CONTACT_SALES}
              component={ContactSales}
            />
  
            <Route
              exact
              path={LOGIN_URL}
              component={LoginPage}
            />
  
            <Redirect to={HOME_URL} />
          </Switch>
  
          {ReactDOM.createPortal(<ContactWidget />, document.getElementById('widget-root'))}
        </AppWrapper>
      </BrowserRouter>
    </StoreProvider>);
};

export default App;
